/* eslint-disable max-lines */
import { match, useRouteMatch } from 'react-router';

import { assertUnreachable } from '~libs.typescript';

import { createPath, GetParams, GetSearchParams, useParams, useSearchParams } from './pathCreator';

/**
 * Appointments
 */

export const APPOINTMENTS_PATH = '/wizyty';
export const APPOINTMENTS_DATE_SEARCH_PARAM = 'data';
export const APPOINTMENTS_DATE_TODAY_SEARCH_PARAM_VALUE = 'dzisiaj';
export const APPOINTMENTS_PATIENT_SEARCH_PARAM = 'pacjent';
export const APPOINTMENTS_PRACTITIONER_SEARCH_PARAM = 'specjalisci';
export const VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM = 'statusy-wizyt';
export const VISIT_ENCOUNTER_MODE_SEARCH_PARAM = 'typy-wizyt';
export const APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM = 'statusy-potwierdzenia';
export const PATIENT_PRESENCE_SEARCH_PARAM = 'obecnosc-pacjenta';
export const PATIENT_GROUP_SEARCH_PARAM = 'oznaczenia-pacjenta';

type AppointmentsListSearchParams = {
   date?: string;
   patientId?: number;
   practitionerIds?: string;
};

export const getAppointmentsListPath = (data?: { searchParams: AppointmentsListSearchParams }): string => {
   return createPath(
      APPOINTMENTS_PATH,
      data ?? {
         searchParams: {
            date: APPOINTMENTS_DATE_TODAY_SEARCH_PARAM_VALUE,
         },
      },
      {
         date: APPOINTMENTS_DATE_SEARCH_PARAM,
         patientId: APPOINTMENTS_PATIENT_SEARCH_PARAM,
         practitionerIds: APPOINTMENTS_PRACTITIONER_SEARCH_PARAM,
      }
   );
};
export const useAppointmentsListSearchParams = (): GetSearchParams<typeof getAppointmentsListPath> =>
   useSearchParams<typeof getAppointmentsListPath>({
      [APPOINTMENTS_DATE_SEARCH_PARAM]: 'date',
      [APPOINTMENTS_PATIENT_SEARCH_PARAM]: 'patientId',
      [APPOINTMENTS_PRACTITIONER_SEARCH_PARAM]: 'practitionerIds',
   });
/**
 * Visit
 */

export const VISIT_PATH = '/wizyta/:visitId' as const;
const getVisitPath = (data: { params: { visitId: number } }): string => createPath(VISIT_PATH, data, undefined);
export const useVisitParams = (): GetParams<typeof getVisitPath> => useParams();
/**
 * Prescription visit
 */

export const PRESCRIPTION_VISIT_PATH = '/wizyta-receptowa/:visitId' as const;
export const getPrescriptionVisitPath = (data: { params: { visitId: number } }): string =>
   createPath(PRESCRIPTION_VISIT_PATH, data, undefined);
export const usePrescriptionVisitParams = (): GetParams<typeof getPrescriptionVisitPath> => useParams();
/**
 * Visit interview
 */

export const VISIT_INTERVIEW_PATH = `${VISIT_PATH}/okno-wizyty` as const;
export const getVisitInterviewPath = (data: { params: { visitId: number } }): string =>
   createPath(VISIT_INTERVIEW_PATH, data, undefined);
export const useVisitInterviewParams = (): GetParams<typeof getVisitInterviewPath> => useParams();

export type VisitType = 'standard' | 'prescription';

export const getVisitEntryPath = (visitId: number, visitType: VisitType = 'standard'): string => {
   switch (visitType) {
      case 'standard':
         return getVisitInterviewPath({ params: { visitId: visitId } });
      case 'prescription':
         return getPrescriptionVisitPath({ params: { visitId: visitId } });
      default:
         return assertUnreachable(visitType);
   }
};

/**
 * Complete patient data
 */
// TODO DREGAB-24438 The transfer of patient Id should be done through the state, not the URL (similar to appointmentId)
export const COMPLETE_PATIENT_DATA_PATH = '/wizyta/uzupelnij-dane-pacjenta/:patientId' as const;
export const getCompletePatientDataPath = (data: { params: { patientId: number } }): string =>
   createPath(COMPLETE_PATIENT_DATA_PATH, data, undefined);
export const useCompletePatientDataPath = (): GetParams<typeof getCompletePatientDataPath> => useParams();

/**
 * Prescriptions
 */

export const PRESCRIPTIONS_PATH = `${VISIT_PATH}/erecepta` as const;
export const getPrescriptionsPath = (data: { params: { visitId: number } }): string =>
   createPath(PRESCRIPTIONS_PATH, data, undefined);
export const usePrescriptionsParams = (): GetParams<typeof getPrescriptionsPath> => useParams();
/**
 * Medical leave
 */

export const MEDICAL_LEAVE_PATH = `${VISIT_PATH}/ezwolnienie` as const;
export const getMedicalLeavePath = (data: { params: { visitId: number } }): string =>
   createPath(MEDICAL_LEAVE_PATH, data, undefined);
export const useMedicalLeaveParams = (): GetParams<typeof getMedicalLeavePath> => useParams();
/**
 * Medical certificate
 */

export const MEDICAL_CERTIFICATE_PATH = `${VISIT_PATH}/zaswiadczenie-lekarskie` as const;
export const getMedicalCertificatePath = (data: { params: { visitId: number } }): string =>
   createPath(MEDICAL_CERTIFICATE_PATH, data, undefined);
export const useMedicalCertificateParams = (): GetParams<typeof getMedicalCertificatePath> => useParams();
/**
 * Referrals
 */

export const REFERRAL_TO_HOSPITAL_PATH = `${VISIT_PATH}/skierowanie-do-szpitala` as const;
export const getReferralToHospitalPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_HOSPITAL_PATH, data, undefined);
export const useReferralToHospitalParams = (): GetParams<typeof getReferralToHospitalPath> => useParams();

export const ELECTRONIC_REFERRAL_TO_HOSPITAL_PATH = `${VISIT_PATH}/eskierowanie-do-szpitala`;
export const getElectronicReferralToHospitalPath = (data: { params: { visitId: number } }): string =>
   createPath(ELECTRONIC_REFERRAL_TO_HOSPITAL_PATH, data, undefined);
export const useElectronicReferralToHospitalParams = (): GetParams<typeof getElectronicReferralToHospitalPath> =>
   useParams();

export const REFERRAL_TO_PSYCHIATRIC_HOSPITAL_PATH = `${VISIT_PATH}/skierowanie-do-szpitala-psychiatrycznego`;
export const getReferralToPsychiatricHospitalPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_PSYCHIATRIC_HOSPITAL_PATH, data, undefined);
export const useReferralToPsychiatricHospitalParams = (): GetParams<typeof getReferralToPsychiatricHospitalPath> =>
   useParams();

export const REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH = `${VISIT_PATH}/skierowanie-na-badania-diagnostyczne`;
export const getReferralToDiagnosticExaminationPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH, data, undefined);
export const useReferralToDiagnosticExaminationParams = (): GetParams<typeof getReferralToDiagnosticExaminationPath> =>
   useParams();

export const ELECTRONIC_REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH = `${VISIT_PATH}/eskierowanie-na-badania-diagnostyczne`;
export const getElectronicReferralToDiagnosticExaminationPath = (data: { params: { visitId: number } }): string =>
   createPath(ELECTRONIC_REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH, data, undefined);
export const useElectronicReferralToDiagnosticExaminationParams = (): GetParams<
   typeof getElectronicReferralToDiagnosticExaminationPath
> => useParams();

export const REFERRAL_TO_ANALYTICAL_EXAMINATION_PATH = `${VISIT_PATH}/skierowanie-do-laboratorium`;
export const getReferralToAnalyticalExaminationPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_ANALYTICAL_EXAMINATION_PATH, data, undefined);
export const useReferralToAnalyticalExaminationParams = (): GetParams<typeof getReferralToAnalyticalExaminationPath> =>
   useParams();

export const REFERRAL_TO_SPECIALIST_PATH = `${VISIT_PATH}/skierowanie-do-specjalisty`;
export const getReferralToSpecialistPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_SPECIALIST_PATH, data, undefined);
export const useReferralToSpecialistParams = (): GetParams<typeof getReferralToSpecialistPath> => useParams();

export const ELECTRONIC_REFERRAL_TO_SPECIALIST_PATH = `${VISIT_PATH}/eskierowanie-do-specjalisty`;
export const getElectronicReferralToSpecialistPath = (data: { params: { visitId: number } }): string =>
   createPath(ELECTRONIC_REFERRAL_TO_SPECIALIST_PATH, data, undefined);
export const useElectronicToSpecialistParams = (): GetParams<typeof getElectronicReferralToSpecialistPath> =>
   useParams();

export const DISCHARGE_SUMMARY_PATH = `${VISIT_PATH}/informacja-dla-lekarza-kierujacego` as const;
export const getDischargeSummaryPath = (data: { params: { visitId: number } }): string =>
   createPath(DISCHARGE_SUMMARY_PATH, data, undefined);

export const REFERRAL_TO_REHABILITATION_PATH = `${VISIT_PATH}/skierowanie-na-rehabilitacje`;
export const getReferralToRehabilitationPath = (data: { params: { visitId: number } }): string =>
   createPath(REFERRAL_TO_REHABILITATION_PATH, data, undefined);
export const useReferralToRehabilitationParams = (): GetParams<typeof getReferralToRehabilitationPath> => useParams();

export const DOCUMENT_TEMPLATE_PATH = `${VISIT_PATH}/dokument-medyczny/:printTemplateId`;
export const getDocumentTemplatePath = (data: {
   params: { visitId: number; printTemplateId: number | undefined };
}): string => createPath(DOCUMENT_TEMPLATE_PATH, data, undefined);
export const useDocumentTemplateParams = (): GetParams<typeof getDocumentTemplatePath> => useParams();

export const CONSENT_TEMPLATE_PATH = `${VISIT_PATH}/zgoda/:printTemplateId`;
export const getConsentTemplatePath = (data: {
   params: { visitId: number; printTemplateId: number | undefined };
}): string => createPath(CONSENT_TEMPLATE_PATH, data, undefined);
export const useConsentTemplateParams = (): GetParams<typeof getConsentTemplatePath> => useParams();
/**
 * Medical devices
 */

export const VISIT_MEDICAL_DEVICES_PATH = `${VISIT_PATH}/wyroby-medyczne` as const;

export enum MedicalDevicesPanelNavParams {
   NEW_ORDER = 'nowe-zlecenie',
   ORDERS_HISTORY = 'historia-zlecen',
}

export const getMedicalDevicesPath = (data: { params: { visitId: number } }): string =>
   createPath(VISIT_MEDICAL_DEVICES_PATH, data, undefined);
export const getMedicalDeviceOrderPath = (data: {
   searchParams: {
      orderId?: number;
   };
   params: {
      visitId: number;
   };
}): string =>
   createPath(
      `${getMedicalDevicesPath({ params: { visitId: data.params.visitId } })}/${
         MedicalDevicesPanelNavParams.NEW_ORDER
      }`,
      data,
      {
         orderId: 'zlecenie',
      }
   );

export const useMedicalDeviceOrderSearchParams = (): GetSearchParams<typeof getMedicalDeviceOrderPath> =>
   useSearchParams<typeof getMedicalDeviceOrderPath>({
      zlecenie: 'orderId',
   });
/**
 * Visit history
 */

export const VISIT_HISTORY_PATH = '/historia-pacjenta/:patientId';
export const START_VISIT_PARAM_NAME = 'wizyta';

export const getVisitHistoryPath = (data: {
   params: {
      patientId: number;
   };
   searchParams: {
      visitId?: number;
   };
}): string =>
   createPath(VISIT_HISTORY_PATH, data, {
      visitId: 'wizyta',
   });
export const useVisitHistoryParams = (): GetParams<typeof getVisitHistoryPath> => useParams();
export const useVisitHistorySearchParams = (): GetSearchParams<typeof getVisitHistoryPath> =>
   useSearchParams<typeof getVisitHistoryPath>({
      wizyta: 'visitId',
   });
/**
 * Calendar
 */

export const CALENDAR_PATH = '/kalendarz';
export const CALENDAR_WAITLIST_PATH = `${CALENDAR_PATH}/lista-rezerwowa`;
// TODO DREGAB-13809 Create a mechanism to remove the parameter from the link
export const CALENDAR_DATE_SEARCH_PARAM = 'data';
export const APPOINTMENT_TERM_UPDATE_SEARCH_PARAM = 'zmiana-terminu';
export const APPOINTMENT_RESTORE_SEARCH_PARAM = 'zaplanuj-ponownie';
export const APPOINTMENT_BOOK_PATIENT_PARAM = 'zarejestruj';
export const APPOINTMENT_WAITLIST_ID_PARAM = 'lista-rezerwowa';
export const APPOINTMENT_PRACTITIONER_PARAM = 'specjalista';

export const getCalendarPath = (data: {
   searchParams: {
      date?: string;
      bookPatientId?: number;
      updateTermAppointmentId?: number;
      restoreAppointmentId?: number;
      waitlistEntryId?: number;
      practitionerId?: number;
   };
}): string =>
   createPath(CALENDAR_PATH, data, {
      date: CALENDAR_DATE_SEARCH_PARAM,
      bookPatientId: APPOINTMENT_BOOK_PATIENT_PARAM,
      updateTermAppointmentId: APPOINTMENT_TERM_UPDATE_SEARCH_PARAM,
      restoreAppointmentId: APPOINTMENT_RESTORE_SEARCH_PARAM,
      waitlistEntryId: APPOINTMENT_WAITLIST_ID_PARAM,
      practitionerId: APPOINTMENT_PRACTITIONER_PARAM,
   });

export const useCalendarSearchParams = (): GetSearchParams<typeof getCalendarPath> =>
   useSearchParams<typeof getCalendarPath>({
      [CALENDAR_DATE_SEARCH_PARAM]: 'date',
      [APPOINTMENT_BOOK_PATIENT_PARAM]: 'bookPatientId',
      [APPOINTMENT_TERM_UPDATE_SEARCH_PARAM]: 'updateTermAppointmentId',
      [APPOINTMENT_RESTORE_SEARCH_PARAM]: 'restoreAppointmentId',
      [APPOINTMENT_WAITLIST_ID_PARAM]: 'waitlistEntryId',
      [APPOINTMENT_PRACTITIONER_PARAM]: 'practitionerId',
   });

/**
 * Prescription Visits
 */

export const PRESCRIPTION_VISITS_PATH = '/wizyty-receptowe';
export const getPrescriptionVisitsPath = (): string =>
   createPath(
      PRESCRIPTION_VISITS_PATH,
      {
         searchParams: {
            date: APPOINTMENTS_DATE_TODAY_SEARCH_PARAM_VALUE,
         },
      },
      {
         date: APPOINTMENTS_DATE_SEARCH_PARAM,
      }
   );

export const getVisitPaths = (): string[] => [APPOINTMENTS_PATH, VISIT_PATH, VISIT_HISTORY_PATH];

export enum PrescriptionPanelPaths {
   MEDICINE_SEARCH = 'wyszukaj-lek',
   MAGISTRAL_DRUG = 'lek-recepturowy',
   DOWNLOAD_HISTORY = 'pobierz-historie',
}

const PRESCRIPTIONS_HISTORY_DOWNLOAD_ACCESS_MODE_SEARCH_PARAM = 'tryb-dostepu';
const PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH = `${PRESCRIPTIONS_PATH}/${PrescriptionPanelPaths.DOWNLOAD_HISTORY}`;
const PRESCRIPTION_VISIT_PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH = `${PRESCRIPTION_VISIT_PATH}/${PrescriptionPanelPaths.DOWNLOAD_HISTORY}`;
type PrescriptionsHistoryDownloadPathData = {
   params: {
      visitId: number;
   };
   searchParams: {
      accessMode?: string;
   };
};
export const getPrescriptionsHistoryDownloadPath = (data: PrescriptionsHistoryDownloadPathData): string => {
   return createPath(PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH, data, {
      accessMode: PRESCRIPTIONS_HISTORY_DOWNLOAD_ACCESS_MODE_SEARCH_PARAM,
   });
};

export const getPrescriptionVisitPrescriptionsHistoryDownloadPath = (
   data: PrescriptionsHistoryDownloadPathData
): string => {
   return createPath(PRESCRIPTION_VISIT_PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH, data, {
      accessMode: PRESCRIPTIONS_HISTORY_DOWNLOAD_ACCESS_MODE_SEARCH_PARAM,
   });
};

export const usePrescriptionsHistoryDownloadPathMatch = (): match | null => {
   return useRouteMatch([PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH, PRESCRIPTION_VISIT_PRESCRIPTIONS_HISTORY_DOWNLOAD_PATH]);
};

export const usePrescriptionsHistoryDownloadSearchParams = (): GetSearchParams<
   typeof getPrescriptionsHistoryDownloadPath
> =>
   useSearchParams<typeof getPrescriptionsHistoryDownloadPath>({
      [PRESCRIPTIONS_HISTORY_DOWNLOAD_ACCESS_MODE_SEARCH_PARAM]: 'accessMode',
   });

/**
 * EducationalMaterials
 */

export const VISIT_EDUCATIONAL_MATERIALS_PATH = `${VISIT_PATH}/materialy-dla-pacjenta` as const;
export const getEducationalMaterialsPath = (data: { params: { visitId: number } }): string =>
   createPath(VISIT_EDUCATIONAL_MATERIALS_PATH, data, undefined);
export const useVisitEducationalMaterialsParams = (): GetParams<typeof getEducationalMaterialsPath> => useParams();
